import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { LANG_CODES, langName } from "../i18n/languages";
import Sidebar from "../components/Sidebar";
import {
  User, Briefcase, Monitor, CheckCircle2,
  ChevronLeft, ChevronRight, ChevronDown, Save, HelpCircle, Loader2, Check,
  Camera, Trash2, MapPin,
} from "lucide-react";

const STEPS = [
  { key: "perso",   label: "Informations personnelles",   icon: User,         sub: "Vos coordonnées et adresse" },
  { key: "pro",     label: "Informations professionnelles", icon: Briefcase,  sub: "Expériences, diplômes et mobilité" },
  { key: "skills",  label: "Compétences & logiciels",      icon: Monitor,     sub: "Vos compétences métier" },
  { key: "recap",   label: "Récapitulatif",                icon: CheckCircle2, sub: "Vérifiez et validez" },
];

const REFERRAL_SOURCES = ["Par du bouche à oreille", "Réseau social", "Google", "Recommandation collègue", "Publicité", "Autre"];
const PHONE_COUNTRIES = [
  "FR (+33)", "AD (+376)", "AL (+355)", "AT (+43)", "BA (+387)", "BE (+32)", "BG (+359)",
  "CH (+41)", "CY (+357)", "CZ (+420)", "DE (+49)", "DK (+45)", "EE (+372)", "ES (+34)",
  "FI (+358)", "GB (+44)", "GR (+30)", "HR (+385)", "HU (+36)", "IE (+353)", "IS (+354)",
  "IT (+39)", "LI (+423)", "LT (+370)", "LU (+352)", "LV (+371)", "MC (+377)", "MD (+373)",
  "ME (+382)", "MK (+389)", "MT (+356)", "NL (+31)", "NO (+47)", "PL (+48)", "PT (+351)",
  "RO (+40)", "RS (+381)", "SE (+46)", "SI (+386)", "SK (+421)", "SM (+378)", "UA (+380)",
];
const RECENT_EXP_OPTS  = ["Oui", "Non"];
const NEW_MISSIONS     = ["Vaccination", "Tests de dépistage (TROD angine)", "Tests de dépistage (TROD cystite)", "Prescription", "Entretiens pharmaceutiques", "Tests allergies", "Tests cholestérol", "Otoscopie", "Test glycémie"];

const REQ_LABELS = {
  civility: "Civilité", first_name: "Prénom", last_name: "Nom", phone: "Téléphone",
  referral_source: "Comment nous avez-vous connu ?", address: "Adresse postale",
  postal_code: "Code postal", city: "Ville", professional_role: "Profil",
  recent_officine_experience: "Expérience en officine", recent_experience_dates: "Dates essentielles",
  softwares: "Maîtrise des logiciels", languages: "Langues parlées",
  itinerant_replacement: "Communauté de remplaçants itinérants",
};

const MOBILITY_COUNTRIES = [
  { code: "FR", name: "France" }, { code: "DE", name: "Allemagne" }, { code: "AT", name: "Autriche" },
  { code: "BE", name: "Belgique" }, { code: "BG", name: "Bulgarie" }, { code: "CY", name: "Chypre" },
  { code: "HR", name: "Croatie" }, { code: "DK", name: "Danemark" }, { code: "ES", name: "Espagne" },
  { code: "EE", name: "Estonie" }, { code: "FI", name: "Finlande" }, { code: "GR", name: "Grèce" },
  { code: "HU", name: "Hongrie" }, { code: "IE", name: "Irlande" }, { code: "IT", name: "Italie" },
  { code: "LV", name: "Lettonie" }, { code: "LT", name: "Lituanie" }, { code: "LU", name: "Luxembourg" },
  { code: "MT", name: "Malte" }, { code: "NL", name: "Pays-Bas" }, { code: "PL", name: "Pologne" },
  { code: "PT", name: "Portugal" }, { code: "CZ", name: "République tchèque" }, { code: "RO", name: "Roumanie" },
  { code: "SK", name: "Slovaquie" }, { code: "SI", name: "Slovénie" }, { code: "SE", name: "Suède" },
  { code: "CH", name: "Suisse" },
];

function stepMissing(i, f) {
  const miss = [];
  if (i === 0) {
    ["civility", "first_name", "last_name", "referral_source", "address", "postal_code", "city"].forEach((k) => {
      if (!String(f[k] || "").trim()) miss.push(k);
    });
    const digits = String(f.phone || "").replace(/\D/g, "");
    if (digits.length < 6 || digits.length > 12) miss.push("phone");
  } else if (i === 1) {
    if (!f.professional_role) miss.push("professional_role");
    if (!f.recent_officine_experience) miss.push("recent_officine_experience");
    if (f.recent_officine_experience !== "Non" && !String(f.recent_experience_dates || "").trim()) miss.push("recent_experience_dates");
  } else if (i === 2) {
    if (!f.softwares.length) miss.push("softwares");
    if (!f.languages.length) miss.push("languages");
    if (!f.itinerant_replacement) miss.push("itinerant_replacement");
  }
  return miss;
}

const flagUrl = (c) => `https://flagcdn.com/w20/${c.slice(0, 2).toLowerCase()}.png`;

function PhoneCountrySelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  return (
    <div ref={boxRef} className="relative shrink-0" style={{ width: "140px", flex: "0 0 auto" }}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="field-phone-country"
        className="rp-input flex items-center gap-2 text-left">
        <img src={flagUrl(value)} alt="" className="w-5 h-auto rounded-[2px] shrink-0" />
        <span className="truncate">{value}</span>
        <ChevronDown className="w-3.5 h-3.5 ml-auto text-[#6B726A] shrink-0" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-[190px] max-h-64 overflow-y-auto bg-white border border-[#E2E5E0] rounded-xl shadow-lg py-1" data-testid="phone-country-list">
          {PHONE_COUNTRIES.map((c) => (
            <button key={c} type="button" onClick={() => { onChange(c); setOpen(false); }}
              data-testid={`phone-country-${c.slice(0, 2)}`}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-[#F7F8F6] ${c === value ? "bg-[#E8F0EA] font-medium" : ""}`}>
              <img src={flagUrl(c)} alt="" className="w-5 h-auto rounded-[2px] shrink-0" />
              {c}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProfileWizard() {
  const { user, refreshUser } = useAuth();
  const { i18n } = useTranslation();
  const isPharmacy = user?.role === "pharmacy";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialStep = Math.max(0, Math.min(STEPS.length - 1, parseInt(searchParams.get("step") || "0", 10) || 0));
  const [ref, setRef] = useState(null);
  const [step, setStep] = useState(initialStep);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState("");
  const fileInputRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
  const avatarSrc = user?.avatar
    ? (user.avatar.startsWith("http") ? user.avatar : `${BACKEND_URL}${user.avatar}`)
    : null;

  const onPickAvatar = () => fileInputRef.current?.click();

  const onAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting same file
    if (!file) return;
    setAvatarError("");
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setAvatarError("Format non supporté (JPG, PNG, WEBP)");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setAvatarError("Image trop volumineuse (max 2 Mo)");
      return;
    }
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/auth/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshUser();
    } catch (err) {
      setAvatarError(err?.response?.data?.detail || "Échec du téléchargement");
    } finally {
      setAvatarBusy(false);
    }
  };

  const removeAvatar = async () => {
    setAvatarError("");
    setAvatarBusy(true);
    try {
      await api.delete("/auth/avatar");
      await refreshUser();
    } catch (err) {
      setAvatarError(err?.response?.data?.detail || "Échec de la suppression");
    } finally {
      setAvatarBusy(false);
    }
  };

  const [form, setForm] = useState({
    civility: user?.civility || "Madame",
    first_name: user?.first_name || (user?.name || "").split(" ")[0] || "",
    last_name: user?.last_name || (user?.name || "").split(" ").slice(1).join(" ") || "",
    email: user?.email || "",
    phone_country: user?.phone_country || "FR (+33)",
    phone: user?.phone || "",
    referral_source: user?.referral_source || REFERRAL_SOURCES[0],
    address: user?.address || "",
    postal_code: user?.postal_code || "",
    city: user?.city || "",
    professional_role: user?.professional_role || "Préparateur en pharmacie",
    recent_officine_experience: user?.recent_officine_experience || "Oui",
    recent_experience_dates: user?.recent_experience_dates || "",
    experience_years: user?.experience_years || 0,
    softwares: user?.softwares || [],
    languages: user?.languages || ["fr"],
    new_missions: user?.new_missions || [],
    itinerant_replacement: user?.itinerant_replacement || "Oui, je suis partant(e) pour découvrir de nouvelles régions",
    max_distance_km: user?.max_distance_km || 50,
    mobility_countries: user?.mobility_countries?.length ? user.mobility_countries : ["FR"],
    vehicle: !!user?.vehicle,
    pharmacy_name: user?.pharmacy_name || "",
  });

  useEffect(() => { api.get("/reference").then(({ data }) => setRef(data)); }, []);

  const [missing, setMissing] = useState([]);

  const upd = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    setMissing((m) => m.filter((x) => x !== k));
  };
  const toggleIn = (key, val) => {
    setForm((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));
    setMissing((m) => m.filter((x) => x !== key));
  };
  const errCls = (k) => (missing.includes(k) ? " rp-input-error" : "");

  const goToStep = (target) => {
    if (target <= step) { setMissing([]); setStep(target); return; }
    for (let i = step; i < target; i++) {
      const miss = stepMissing(i, form);
      if (miss.length) { setStep(i); setMissing(miss); return; }
    }
    setMissing([]);
    setStep(target);
  };

  const validateAllAndSave = () => {
    for (let i = 0; i < 3; i++) {
      const miss = stepMissing(i, form);
      if (miss.length) { setStep(i); setMissing(miss); return; }
    }
    setMissing([]);
    save(true);
  };

  const save = async (andExit = false) => {
    setBusy(true);
    try {
      const distanceNum = parseInt(String(form.max_distance_km)) || 50;
      const payload = {
        ...form,
        name: `${form.first_name} ${form.last_name}`.trim(),
        experience_years: parseInt(form.experience_years) || 0,
        max_distance_km: distanceNum,
      };
      await api.patch("/auth/profile", payload);
      await refreshUser();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      if (andExit) navigate("/profile");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex bg-[#F7F8F6] min-h-screen">
      {isPharmacy && <Sidebar />}
      <main className="flex-1 min-w-0">
        <div className="max-w-7xl mx-auto p-4 lg:p-10 pb-24 lg:pb-10">
          {/* Header */}
          <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#E8F0EA] grid place-items-center"><User className="w-5 h-5 text-[#1A4D2E]" /></div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-medium tracking-tight" style={{ fontFamily: "Outfit" }}>Créer mon profil</h1>
                <p className="text-sm text-[#6B726A] mt-1">Complétez votre profil pour être visible des recruteurs et recevoir des offres adaptées.</p>
              </div>
            </div>
            <button onClick={() => save(true)} disabled={busy} data-testid="profile-save-exit"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#1A4D2E] text-[#1A4D2E] text-sm font-medium hover:bg-[#E8F0EA] disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer et quitter
            </button>
          </div>

          {/* Horizontal stepper */}
          <div className="bg-white rounded-2xl border border-[#E2E5E0] p-4 sm:p-6 mb-6 overflow-x-auto" data-testid="stepper">
            <div className="flex items-center min-w-[700px]">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                const isActive = i === step;
                const isDone = i < step;
                return (
                  <button key={s.key} onClick={() => goToStep(i)} className="flex-1 group" data-testid={`step-tab-${s.key}`}>
                    <div className="flex items-center">
                      <div className={`w-9 h-9 rounded-full grid place-items-center text-sm font-semibold ${
                        isActive ? "bg-[#1A4D2E] text-white" : isDone ? "bg-[#E8F0EA] text-[#1A4D2E]" : "bg-[#F0F2EF] text-[#6B726A]"
                      }`}>
                        {isDone ? <Check className="w-4 h-4" /> : i + 1}
                      </div>
                      {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-2 ${isDone ? "bg-[#1A4D2E]" : "bg-[#E2E5E0]"}`} />}
                    </div>
                    <div className={`mt-2 text-xs font-medium text-left ${isActive ? "text-[#1A4D2E]" : "text-[#6B726A]"}`}>{s.label}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid lg:grid-cols-[260px_1fr] gap-6">
            {/* Left vertical menu */}
            <aside className="space-y-2" data-testid="profile-menu">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                const isActive = i === step;
                return (
                  <button key={s.key} onClick={() => goToStep(i)} data-testid={`menu-${s.key}`}
                    className={`w-full text-left p-3 rounded-2xl flex items-start gap-3 transition-colors ${
                      isActive ? "bg-[#E8F0EA] border border-[#1A4D2E]" : "bg-white border border-[#E2E5E0] hover:border-[#1A4D2E]/40"
                    }`}>
                    <Icon className={`w-5 h-5 mt-0.5 ${isActive ? "text-[#1A4D2E]" : "text-[#6B726A]"}`} />
                    <div className="min-w-0">
                      <div className={`text-sm font-medium ${isActive ? "text-[#1A4D2E]" : "text-[#1f2421]"}`}>{s.label}</div>
                      <div className="text-xs text-[#6B726A] mt-0.5">{s.sub}</div>
                    </div>
                  </button>
                );
              })}
              <div className="bg-white border border-[#E2E5E0] rounded-2xl p-4 mt-6">
                <div className="text-sm font-medium mb-1" style={{ fontFamily: "Outfit" }}>Besoin d&apos;aide ?</div>
                <p className="text-xs text-[#6B726A] mb-3">Notre équipe est à votre disposition pour vous accompagner.</p>
                <a href="mailto:support@apocia.com" className="inline-flex items-center gap-2 text-xs border border-[#E2E5E0] rounded-full px-3 py-1.5 hover:bg-[#F7F8F6]">
                  <HelpCircle className="w-3.5 h-3.5" /> Nous contacter
                </a>
              </div>
            </aside>

            {/* Right content */}
            <div className="space-y-6">
              {step === 0 && (
                <Card title="Informations personnelles">
                  {/* Avatar uploader */}
                  <div className="flex items-center gap-5 pb-5 border-b border-dashed border-[#E2E5E0]" data-testid="avatar-block">
                    <div className="relative">
                      <div className="w-24 h-24 rounded-full overflow-hidden bg-[#E8F0EA] grid place-items-center border-4 border-white shadow ring-1 ring-[#E2E5E0]">
                        {avatarSrc ? (
                          <img src={avatarSrc} alt="avatar" className="w-full h-full object-cover" data-testid="avatar-preview" />
                        ) : (
                          <span className="text-2xl font-semibold text-[#1A4D2E]">
                            {((form.first_name?.[0] || "") + (form.last_name?.[0] || "")).toUpperCase() || "?"}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={onPickAvatar}
                        disabled={avatarBusy}
                        data-testid="avatar-pick-btn"
                        className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-[#1A4D2E] text-white grid place-items-center shadow hover:bg-[#143C24] disabled:opacity-60"
                        aria-label="Changer la photo"
                      >
                        {avatarBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                      </button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium" style={{ fontFamily: "Outfit" }}>Photo de profil</div>
                      <p className="text-xs text-[#6B726A] mt-0.5">JPG, PNG ou WEBP. 2 Mo max. Une photo claire augmente la confiance des recruteurs.</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button type="button" onClick={onPickAvatar} disabled={avatarBusy} data-testid="avatar-upload-btn"
                          className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#E8F0EA] disabled:opacity-60">
                          <Camera className="w-3.5 h-3.5" /> {avatarSrc ? "Changer" : "Téléverser"}
                        </button>
                        {avatarSrc && (
                          <button type="button" onClick={removeAvatar} disabled={avatarBusy} data-testid="avatar-remove-btn"
                            className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-[#E2E5E0] text-[#6B726A] hover:bg-[#F7F8F6] disabled:opacity-60">
                            <Trash2 className="w-3.5 h-3.5" /> Supprimer
                          </button>
                        )}
                      </div>
                      {avatarError && (
                        <div className="mt-2 text-xs text-[#E3655B]" data-testid="avatar-error">{avatarError}</div>
                      )}
                      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp"
                        onChange={onAvatarChange} className="hidden" data-testid="avatar-file-input" />
                    </div>
                  </div>

                  <Field label="Civilité *">
                    <div className="flex flex-wrap gap-3" data-testid="field-civility">
                      {["Monsieur", "Madame", "Ne se prononce pas"].map((c) => (
                        <Radio key={c} label={c} checked={form.civility === c} onChange={() => setForm({ ...form, civility: c })} />
                      ))}
                    </div>
                  </Field>
                  <Two>
                    <Field label="Prénom *"><input className={`rp-input${errCls("first_name")}`} data-testid="field-first_name" value={form.first_name} onChange={upd("first_name")} /></Field>
                    <Field label="Nom *"><input className={`rp-input${errCls("last_name")}`} data-testid="field-last_name" value={form.last_name} onChange={upd("last_name")} /></Field>
                  </Two>
                  <Two>
                    <Field label="Email *"><input type="email" className="rp-input" value={form.email} disabled /></Field>
                    <Field label="Téléphone *">
                      <div className="flex gap-2 items-stretch">
                        <PhoneCountrySelect value={form.phone_country} onChange={(c) => setForm({ ...form, phone_country: c })} />
                        <input className={`rp-input${errCls("phone")}`} style={{ flex: "1 1 auto", width: "auto", minWidth: 0 }}
                          type="tel" inputMode="tel" data-testid="field-phone" value={form.phone}
                          onChange={(e) => {
                            const v = e.target.value.replace(/[^\d\s]/g, "").slice(0, 15);
                            setForm({ ...form, phone: v });
                            setMissing((m) => m.filter((x) => x !== "phone"));
                          }}
                          placeholder="6 12 34 56 78" />
                      </div>
                    </Field>
                  </Two>
                  <Two>
                    <Field label="Comment nous avez-vous connu ? *">
                      <select className={`rp-input${errCls("referral_source")}`} value={form.referral_source} onChange={upd("referral_source")} data-testid="field-referral">
                        {REFERRAL_SOURCES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </Field>
                    <Field label="Adresse postale *">
                      <input className={`rp-input${errCls("address")}`} data-testid="field-address" value={form.address} onChange={upd("address")} />
                    </Field>
                  </Two>
                  <Two>
                    <Field label="Code postal *"><input className={`rp-input${errCls("postal_code")}`} data-testid="field-postal_code" value={form.postal_code} onChange={upd("postal_code")} /></Field>
                    <Field label="Ville *"><input className={`rp-input${errCls("city")}`} data-testid="field-city" value={form.city} onChange={upd("city")} /></Field>
                  </Two>
                </Card>
              )}

              {step === 1 && (
                <Card title="Informations professionnelles">
                  <Two>
                    <Field label="Quel est votre profil ? *">
                      <select className="rp-input" value={form.professional_role} onChange={upd("professional_role")} data-testid="field-role">
                        {(ref?.professional_roles || []).map((r) => <option key={r}>{r}</option>)}
                      </select>
                    </Field>
                    <Field label="Avez-vous d'autres expériences en officine suite à votre diplôme ? *">
                      <select className="rp-input" value={form.recent_officine_experience} onChange={upd("recent_officine_experience")}>
                        <option>Oui, j&apos;ai exercé en officine suite à mon diplôme</option>
                        <option>Non</option>
                      </select>
                    </Field>
                  </Two>
                  <Two>
                    <Field label="Avez-vous travaillé en officine au cours des 12 derniers mois ? *">
                      <select className="rp-input" value={form.recent_officine_experience} onChange={upd("recent_officine_experience")}>
                        {RECENT_EXP_OPTS.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    </Field>
                    <Field label="Si oui, merci de renseigner les dates essentielles *">
                      <input className={`rp-input${errCls("recent_experience_dates")}`} placeholder="2021 - 2024" value={form.recent_experience_dates} onChange={upd("recent_experience_dates")} data-testid="field-dates" />
                    </Field>
                  </Two>
                  <Field label="Années d'expérience cumulées">
                    <input type="number" min={0} className="rp-input max-w-[160px]" value={form.experience_years} onChange={upd("experience_years")} />
                  </Field>

                  <div className="pt-5 border-t border-dashed border-[#E2E5E0]">
                    <Field label="Mobilité géographique">
                      <div className="space-y-3" data-testid="field-mobility">
                        <div className="flex items-center gap-2 text-sm text-[#0F2A1B]">
                          <MapPin className="w-4 h-4 text-[#1A4D2E]" />
                          <span>Rayon de déplacement autour de votre position&nbsp;: <strong data-testid="mobility-km-value">{form.max_distance_km} km</strong></span>
                        </div>
                        <input
                          type="range" min={5} max={3000} step={5}
                          value={form.max_distance_km}
                          onChange={(e) => setForm({ ...form, max_distance_km: parseInt(e.target.value, 10) })}
                          className="w-full accent-[#1A4D2E]"
                          data-testid="field-distance-slider"
                        />
                        <div className="flex justify-between text-[11px] text-[#6B726A]">
                          <span>5 km</span><span>3000 km</span>
                        </div>
                        <div className="pt-3">
                          <div className="text-xs font-medium text-[#0F2A1B] mb-2">Pays où vous acceptez de travailler (Union européenne + Suisse) *</div>
                          <div className="flex flex-wrap gap-1.5" data-testid="field-mobility-countries">
                            {MOBILITY_COUNTRIES.map((c) => {
                              const on = form.mobility_countries.includes(c.code);
                              return (
                                <button key={c.code} type="button" onClick={() => toggleIn("mobility_countries", c.code)}
                                  data-testid={`mobility-country-${c.code}`}
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs border transition-colors ${
                                    on ? "bg-[#E8F0EA] border-[#1A4D2E] text-[#0F2A1B] font-medium" : "border-[#E2E5E0] text-[#6B726A] hover:border-[#1A4D2E]/40"
                                  }`}>
                                  <img src={`https://flagcdn.com/w20/${c.code.toLowerCase()}.png`} alt="" className="w-4 h-auto rounded-[2px]" />
                                  {c.name}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        <p className="text-xs text-[#6B726A]">
                          Le rayon s&apos;applique autour de votre position, dans les pays sélectionnés.
                        </p>
                        <Checkbox label="Véhicule personnel" checked={form.vehicle} onChange={() => setForm({ ...form, vehicle: !form.vehicle })} />
                      </div>
                    </Field>
                  </div>
                </Card>
              )}

              {step === 2 && (
                <Card title="Compétences & logiciels">
                  <Two>
                    <Field label="Maîtrise des logiciels *">
                      <div className="grid grid-cols-2 gap-2" data-testid="field-softwares">
                        {(ref?.pharmacy_softwares || []).map((s) => (
                          <Checkbox key={s} label={s} checked={form.softwares.includes(s)} onChange={() => toggleIn("softwares", s)} />
                        ))}
                      </div>
                    </Field>
                    <Field label="Nouvelles missions du pharmacien">
                      <div className="grid grid-cols-1 gap-2" data-testid="field-missions">
                        {NEW_MISSIONS.map((s) => (
                          <Checkbox key={s} label={s} checked={form.new_missions.includes(s)} onChange={() => toggleIn("new_missions", s)} />
                        ))}
                      </div>
                    </Field>
                  </Two>
                  <Field label="Langues parlées *">
                    <div className="flex flex-wrap gap-2" data-testid="field-languages">
                      {LANG_CODES.map((c) => (
                        <button key={c} type="button" data-testid={`wizard-lang-${c}`}
                          onClick={() => toggleIn("languages", c)}
                          className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                            form.languages.includes(c)
                              ? "bg-[#E8F0EA] border-[#1A4D2E] text-[#1A4D2E]"
                              : "border-[#E2E5E0] text-[#6B726A] hover:border-[#1A4D2E]/40"
                          }`}>{langName(c, i18n.language)}</button>
                      ))}
                    </div>
                  </Field>
                  <Field label="Souhaitez-vous rejoindre notre communauté de remplaçants itinérants (« sac à dos ») ? *">
                    <select className="rp-input" value={form.itinerant_replacement} onChange={upd("itinerant_replacement")}>
                      <option>Oui, je suis partant(e) pour découvrir de nouvelles régions</option>
                      <option>Non, je préfère rester dans ma zone</option>
                      <option>Peut-être, selon les conditions</option>
                    </select>
                  </Field>
                </Card>
              )}

              {step === 3 && (
                <Card title="Récapitulatif">
                  <KV k="Nom" v={`${form.civility} ${form.first_name} ${form.last_name}`} />
                  <KV k="Coordonnées" v={`${form.email} · ${form.phone_country} ${form.phone}`} />
                  <KV k="Adresse" v={`${form.address}, ${form.postal_code} ${form.city}`} />
                  <KV k="Profil" v={form.professional_role} />
                  <KV k="Expérience" v={`${form.experience_years} an(s) · ${form.recent_experience_dates || "—"}`} />
                  <KV k="Logiciels" v={form.softwares.join(", ") || "—"} />
                  <KV k="Missions" v={form.new_missions.join(", ") || "—"} />
                  <KV k="Mobilité" v={`${form.max_distance_km} km autour de ${form.city || "votre position"} · ${form.mobility_countries.join(", ")}${form.vehicle ? " · véhicule" : ""}`} />
                  <KV k="Itinérant" v={form.itinerant_replacement} />
                  {saved && (
                    <div className="mt-4 text-sm text-[#1A4D2E] bg-[#E8F0EA] border border-[#1A4D2E]/15 rounded-xl px-3 py-2 flex items-center gap-2">
                      <Check className="w-4 h-4" /> Profil enregistré avec succès.
                    </div>
                  )}
                </Card>
              )}

              {/* Footer nav */}
              {missing.length > 0 && (
                <div data-testid="wizard-errors" className="text-sm text-[#B3261E] bg-[#FDECEA] border border-[#E3655B]/40 rounded-xl px-4 py-3">
                  Veuillez remplir les champs obligatoires&nbsp;: {missing.map((k) => REQ_LABELS[k] || k).join(", ")}.
                </div>
              )}
              <div className="flex items-center justify-between">
                <button onClick={() => { setMissing([]); setStep(Math.max(0, step - 1)); }} disabled={step === 0} data-testid="profile-prev"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#E2E5E0] text-sm font-medium hover:bg-[#F7F8F6] disabled:opacity-50">
                  <ChevronLeft className="w-4 h-4" /> Précédent
                </button>
                {step < STEPS.length - 1 ? (
                  <button onClick={() => goToStep(step + 1)} data-testid="profile-next"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1A4D2E] text-white text-sm font-medium hover:bg-[#143C24]">
                    Suivant <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button onClick={validateAllAndSave} disabled={busy} data-testid="profile-validate"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1A4D2E] text-white text-sm font-medium hover:bg-[#143C24] disabled:opacity-60">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Valider mon profil
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <style>{`
        .rp-input{width:100%;background:#fff;border:1px solid #E2E5E0;border-radius:.75rem;padding:.55rem .85rem;outline:none;font-size:.9rem}
        .rp-input:focus{border-color:#1A4D2E;box-shadow:0 0 0 3px rgba(26,77,46,.12)}
        .rp-input-error{border-color:#E3655B!important;box-shadow:0 0 0 3px rgba(227,101,91,.12)}
      `}</style>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E2E5E0] p-6 sm:p-8 space-y-5">
      <h2 className="text-lg font-medium" style={{ fontFamily: "Outfit" }}>{title}</h2>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return <label className="block"><span className="text-xs uppercase tracking-wider font-semibold text-[#6B726A]">{label}</span><div className="mt-2">{children}</div></label>;
}
function Two({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>;
}
function Radio({ label, checked, onChange }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
      <span className={`w-4 h-4 rounded-full border-2 grid place-items-center ${checked ? "border-[#1A4D2E]" : "border-[#C5CBC2]"}`}>
        {checked && <span className="w-2 h-2 rounded-full bg-[#1A4D2E]" />}
      </span>
      <input type="radio" className="sr-only" checked={checked} onChange={onChange} /> {label}
    </label>
  );
}
function Checkbox({ label, checked, onChange }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
      <input type="checkbox" checked={checked} onChange={onChange} className="accent-[#1A4D2E] w-4 h-4" />
      {label}
    </label>
  );
}
function KV({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-dashed border-[#E2E5E0] last:border-b-0">
      <span className="text-xs uppercase tracking-wider text-[#6B726A] shrink-0">{k}</span>
      <span className="text-sm font-medium text-[#1f2421] text-right">{v}</span>
    </div>
  );
}
